import type { SourceReadProfile, SourceReadProfileState } from '../types/api';
/**
 * API Client for Context Harness Backend
 *
 * Features:
 * - Automatic Bearer token injection (Clerk)
 * - Type-safe API calls
 * - Error handling (401, 403, 500)
 * - Base URL configuration via environment variables
 */

import type {
  // ACL Types
  DefaultACLSettings,
  ACLCategory,
  CreateACLCategoryRequest,
  ShareRequest,
  ShareRequestsResponse,
  ApproveRejectRequest,
  // Adapters Types
  AdapterType,
  AdapterInfo,
  AdaptersListResponse,
  AdapterConnectRequest,
  AdapterConnectResponse,
  AdapterDisconnectResponse,
  // BOX Types
  BoxUploadInput,
  FileUploadMetadata,
  BoxUploadResponse,
  BoxStatusResponse,
  BoxHistoryResponse,
  BoxHistoryItem,
  // Proposals Types
  ProposalListResponse,
  ProposalDetailResponse,
  ProposalActionResponse,
  ProposalStatus,
  // Memory Types
  MemoryCategory,
  MemoryItem,
  MemoryLink,
  MemoryResource,
  MemoryAssetExportResponse,
  SearchRequest,
  SearchResponse,
  SearchResultItem,
  SearchStrategy,
  // Workspace Types
  WorkspaceFeaturesResponse,
  WorkspaceFeaturesData,
  WorkspaceStatusResponse,
  CreateOnboardingWorkspaceRequest,
  OnboardingWorkspaceResponse,
  OnboardingAutoSetupResponse,
  OnboardingPreset,
  VisibleWorkspaceInfo,
  VisibleWorkspaceListResponse,
  VisibleWorkspaceResponse,
  // Workspace Admin Types (Q-1)
  WorkspaceListResponse,
  WorkspaceMember,
  WorkspaceMembersResponse,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
  OverallStatsResponse,
  BenchmarkCurationOverviewResponse,
  BenchmarkCurationPacketListResponse,
  BenchmarkCurationPacketListItem,
  BenchmarkCurationPacketDetailResponse,
  BenchmarkCurationPacketDetail,
  BenchmarkCurationQueueResponse,
  BenchmarkCurationQueueEntry,
  BenchmarkCurationPacketUpdateRequest,
  JLocomoCurationOverviewResponse,
  JLocomoCurationPacketListResponse,
  JLocomoCurationPacketListItem,
  JLocomoCurationPacketDetailResponse,
  JLocomoCurationPacketDetail,
  JLocomoCurationQueueResponse,
  JLocomoCurationQueueEntry,
  JLocomoCurationPacketUpdateRequest,
  JLocomoArtifactsStatusResponse,
  JLocomoReadinessResponse,
  JLocomoCurationValidateRequest,
  JLocomoCurationValidateResponse,
  JLocomoCurationCompileResponse,
  JLocomoCurationSmokeRunRequest,
  JLocomoCurationSmokeRunResponse,
  KpiSummaryResponse,
  KpiStatsResponse,
  MonitoringMetricsResponse,
  MonitoringHealthResponse,
  CatalogSummaryResponse,
  // Access Management Types (UAC-08b)
  AccessPolicy,
  AccessPoliciesListResponse,
  CreateAccessPolicyRequest,
  McpToken,
  McpTokensListResponse,
  CreateMcpTokenRequest,
  CreateMcpTokenResponse,
  // userSlot 選択肢 API Types (UCX-04)
  UserSlotsListResponse,
} from '../types/api';
// WU-07: capability bootstrap のレスポンス契約（capability-config.ts が正本）
import type { MeBootstrapResponse } from './capability-config';
import type {
  MeetingSnapshotHistoryResponse,
  MeetingSnapshotResponse,
} from '../types/meeting-center';
import type {
  CanonicalWorkspacePrincipal,
  GiftAuditEventsResponse,
  IssueMcpCredentialInput,
  IssuedMcpCredential,
  ManagedMcpCredential,
  MeetingImportsResponse,
  MeetingImportApprovalTarget,
  MeetingSourceTestResult,
  MeetingSourcesResponse,
  OnboardGiftOperatorInput,
  OnboardGiftOperatorResult,
  OperatorsResponse,
  UpdateMeetingSourceInput,
} from '../types/gift-operations';
import { buildPresetPolicyEntries } from './access-policy-presets';

// ─── Configuration ───

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// ─── URL Building ───

/**
 * Build full API URL from path
 * Handles both relative (/api) and absolute (https://api.example.com) base URLs
 *
 * @example
 * - API_BASE_URL='/api', path='/acl/settings' → '/api/acl/settings'
 * - API_BASE_URL='https://api.example.com', path='/acl/settings' → 'https://api.example.com/acl/settings'
 */
function buildApiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : API_BASE_URL + '/';
  return base + cleanPath;
}

function mutationHeaders(): HeadersInit {
  const idempotencyKey = globalThis.crypto?.randomUUID?.()
    ?? `web-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const correlationId = globalThis.crypto?.randomUUID?.() ?? idempotencyKey
  return { 'Idempotency-Key': idempotencyKey, 'X-Correlation-ID': correlationId }
}

interface RawOperationsPrincipalV2 {
  projectionVersion: 2
  workspaceId: string
  workspaceIdentity: {
    memberId: string | null
    sessionUserId: string
    displayName: string | null
    workspaceRole: string | null
    dataUserSlot: string | null
    state: 'active' | 'inactive' | 'conflict'
    conflicts: string[]
  }
  accessPolicyPrincipal: {
    principalRef: string | null
    state: 'configured' | 'missing' | 'conflict'
  }
  mcpCredentials: {
    state: 'active' | 'missing' | 'conflict'
    items: Array<{
      credentialId: string
      name: string
      keyPrefix: string
      createdAt: string
      lastUsedAt: string | null
      expiresAt: string | null
    }>
  }
}

interface RawOperationsPrincipalLegacy {
  workspaceId: string
  memberId: string | null
  sessionUserId: string
  displayName?: string | null
  workspaceRole: string | null
  dataUserSlot: string | null
  mcpPrincipalIds: string[]
  accessPolicyPrincipalRef: string | null
  status: 'active' | 'inactive' | 'conflict'
  conflicts: string[]
}

type RawOperationsPrincipal = RawOperationsPrincipalV2 | RawOperationsPrincipalLegacy

function mapOperationsPrincipal(
  principal: RawOperationsPrincipal,
): CanonicalWorkspacePrincipal {
  if (!('workspaceIdentity' in principal)) {
    const identityState = principal.status
    const trusted = identityState === 'active'
    return {
      workspaceId: principal.workspaceId,
      workspaceIdentity: {
        memberId: principal.memberId,
        sessionUserId: principal.sessionUserId,
        displayName: principal.displayName ?? null,
        workspaceRole: principal.workspaceRole,
        dataUserSlot: principal.dataUserSlot,
        state: identityState,
        conflicts: principal.conflicts,
      },
      accessPolicyPrincipal: {
        principalRef: trusted ? principal.accessPolicyPrincipalRef : null,
        state: trusted
          ? principal.accessPolicyPrincipalRef
            ? 'configured'
            : 'missing'
          : 'conflict',
      },
      mcpCredentials: {
        state: trusted
          ? principal.mcpPrincipalIds.length > 0
            ? 'active'
            : 'missing'
          : 'conflict',
        items: trusted
          ? principal.mcpPrincipalIds.map((credentialId) => ({
            id: credentialId,
            name: 'MCP credential',
            keyPrefix: null,
            status: 'active',
            createdAt: null,
            lastUsedAt: null,
            expiresAt: null,
          }))
          : [],
      },
    }
  }
  return {
    workspaceId: principal.workspaceId,
    workspaceIdentity: principal.workspaceIdentity,
    accessPolicyPrincipal: principal.accessPolicyPrincipal,
    mcpCredentials: {
      state: principal.mcpCredentials.state,
      items: principal.mcpCredentials.items.map((credential) => ({
        id: credential.credentialId,
        name: credential.name,
        keyPrefix: credential.keyPrefix,
        status: 'active',
        createdAt: credential.createdAt,
        lastUsedAt: credential.lastUsedAt,
        expiresAt: credential.expiresAt,
      })),
    },
  }
}

interface RawMeetingSource {
  id: string
  provider: 'google_meet' | 'zoom_meeting'
  organizerRefs: string[]
  meetingFilter: {
    externalMeetingIds?: string[]
    externalArtifactIds?: string[]
    conferenceRecord?: string
    meetingId?: string
  }
  freshnessSlaMinutes: number
  enabled: boolean
  credentialState: 'configured' | 'unknown'
  connectionState: 'connected' | 'not_configured' | 'error' | 'unknown' | 'disabled'
  lastSucceededAt: string | null
}

interface RawMeetingImport {
  id: string
  provider: 'google_meet' | 'zoom_meeting'
  status: 'detected' | 'fetching' | 'extracting' | 'review_required' | 'approved' | 'rejected' | 'retryable' | 'failed' | 'deduplicated'
  detectedAt: string
  errorCode: string | null
  reviewedAt: string | null
  candidates: Array<{
    id: string
    kind: 'decision' | 'action' | 'issue' | 'agenda' | 'evidence'
    summary: string
    confidence: number
    reviewStatus: 'pending' | 'approved' | 'rejected'
  }>
}

interface RawOperationsAuditEvent {
  id: string
  actorUserId: string
  resourceId: string
  action: string
  timestamp: string
  metadata: Record<string, unknown>
}

// ─── Freshness Types (WU-06) ───
// core /api/freshness の契約と同期（packages/core/src/api/web-ui/freshness.ts）。
// stale 判定（>24h）はサーバー側固定。クライアントは stale フラグを表示に使うだけ。

export type FreshnessKind = 'catalog' | 'funnel' | 'ingest';

export interface FreshnessSource {
  kind: FreshnessKind;
  /** データの最終更新時刻（ISO8601）。1 件もなければ null */
  asOf: string | null;
  /** 経過時間（時間）。asOf=null なら null */
  staleHours: number | null;
  /** 24h 超過または鮮度不明なら true */
  stale: boolean;
}

export interface FreshnessResponse {
  sources: FreshnessSource[];
  generatedAt: string;
}

// ─── Funnel Dashboard Types (GR-04c-4) ───
// core /api/funnel/* の契約と同期（packages/core/src/api/web-ui/funnel-dashboard.ts）。
// 集計値のみ（cb_funnel_snapshots / cb_policy_events）。raw record は流れない。

export interface FunnelStageCounts {
  juchu: number;
  application: number;
  entry: number;
  construction: number;
  activated: number;
  cancelled: number;
  churned: number;
  paid: number;
}

export interface FunnelMonthSlice {
  period: string;
  /** throughput（各イベント日が当月内の件数）。snapshot 欠落月は null */
  keiei: { stageCounts: FunnelStageCounts; metrics: Record<string, unknown> } | null;
  /** 受注月 cohort。snapshot 欠落月は null */
  bunseki: { stageCounts: FunnelStageCounts; metrics: Record<string, unknown> } | null;
}

export interface FunnelOverviewResponse {
  sfOrgId: string;
  periods: string[];
  months: FunnelMonthSlice[];
  computedAt: string | null;
}

export type FunnelSegmentAxis = 'product' | 'product_list' | 'owner';

export interface FunnelSegmentRow {
  period: string;
  segmentKey: string;
  product: string | null;
  list: string | null;
  ownerId: string | null;
  stageCounts: FunnelStageCounts;
}

export interface FunnelSegmentsResponse {
  sfOrgId: string;
  axis: FunnelSegmentAxis;
  periods: string[];
  rows: FunnelSegmentRow[];
}

export interface FunnelPolicyEventComparison {
  beforeAverage: number;
  afterAverage: number;
  diff: number;
  beforeCount: number;
  afterCount: number;
}

export interface FunnelPolicyEventItem {
  id: string;
  name: string;
  eventDate: string;
  description: string | null;
  comparison: FunnelPolicyEventComparison | null;
}

export interface FunnelPolicyEventsResponse {
  sfOrgId: string;
  periods: string[];
  events: FunnelPolicyEventItem[];
}

// ─── GIFT サマリー (GSB) ───
// 契約: packages/core/src/api/web-ui/gift-summary.ts と同期

export type GiftSummaryDataStatus = 'ok' | 'empty' | 'not_connected';

export interface GiftSummaryCallsKpi {
  calls: number;
  connected: number;
  apo: number;
  apoRate: number | null;
  smallDenominator: boolean;
}

export interface GiftSummaryCallsResponse {
  dataStatus: GiftSummaryDataStatus;
  month: string;
  latestDay: string | null;
  latestWorkday: GiftSummaryCallsKpi | null;
  monthKpi: GiftSummaryCallsKpi | null;
  daily: Array<{ day: string; calls: number; apo: number; isHoliday: boolean }>;
  callers: Array<{ caller: string; calls: number; connected: number; apo: number }>;
  ngReasons: Array<{ reason: string; count: number }>;
  computedAt: string | null;
}

export interface GiftSummaryAxStage {
  key: string;
  label: string;
  count: number | null;
  prevCount?: number | null;
}

export interface GiftSummaryAxRangeInfo {
  kind: string;
  fromDay: string;
  toDayExclusive: string;
  label: string;
  prevFromDay: string | null;
  prevToDayExclusive: string | null;
  invalid: boolean;
}

export interface GiftSummaryAxMetric {
  key: string;
  label: string;
  value: number | null;
  prevValue: number | null;
  /** false は「その段を数える配線がまだ無い」。value は null（0 ではない） */
  wired: boolean;
}

export interface GiftSummaryAxMonth {
  month: string;
  calls: number;
  apo: number;
  meetingsHeld: number;
  clMeetings: number;
  closedWon: number;
}

export interface GiftSummaryAxResponse {
  dataStatus: GiftSummaryDataStatus;
  month: string;
  range: GiftSummaryAxRangeInfo;
  metrics: GiftSummaryAxMetric[];
  stages: GiftSummaryAxStage[];
  pipeline: Array<{ stage: string; deals: number; quoteAmountYen: number }>;
  pipelineDay: string | null;
  monthly: GiftSummaryAxMonth[];
  computedAt: string | null;
}

export interface GiftSummaryAxCompany {
  name: string;
  day: string | null;
  timeLabel: string | null;
  kind: string;
  outlook: string;
  outlookTone: 'ok' | 'mid' | 'ng' | 'unknown';
  oneLiner: string;
  speech: string;
  evalFile: string | null;
  transcriptFile: string | null;
  pack: string;
}

export interface GiftSummaryAxPackSummary {
  name: string;
  fromDay: string | null;
  toDay: string | null;
  companies: number;
}

export interface GiftSummaryAxCompaniesResponse {
  dataStatus: GiftSummaryDataStatus;
  fromDay: string;
  toDay: string;
  packs: GiftSummaryAxPackSummary[];
  periodMismatch: boolean;
  undated: number;
  companies: GiftSummaryAxCompany[];
}

export interface GiftSummaryAiSdrResponse {
  dataStatus: GiftSummaryDataStatus;
  aiChannelApo: number;
  computedAt: string | null;
}

export interface GiftSummaryDealPacksResponse {
  dataStatus: GiftSummaryDataStatus;
  packs: Array<{ name: string }>;
}

export interface GiftSummaryOverviewResponse {
  dataStatus: GiftSummaryDataStatus;
  calls: {
    dataStatus: GiftSummaryDataStatus;
    latestDay: string | null;
    monthCalls: number | null;
    monthApo: number | null;
    monthApoRate: number | null;
  };
  ax: {
    dataStatus: GiftSummaryDataStatus;
    stages: GiftSummaryAxStage[];
    pipelineTotalDeals: number | null;
    pipelineTotalQuoteAmountYen: number | null;
  };
  aiSdr: {
    dataStatus: GiftSummaryDataStatus;
    aiChannelApo: number;
  };
  computedAt: string | null;
}

// ─── Team Pulse (TP-02) ───
// 契約: packages/core/src/api/web-ui/team-pulse.ts と同期

export interface TeamPulseTask {
  task: string;
  state: string;
  evidence: string;
}

export interface TeamPulseMember {
  name: string;
  roleNow: string;
  currentTasks: TeamPulseTask[];
  capacity: {
    level: string;
    reason: string;
    changeThisWeek: string;
  };
  waitingOn: string;
  confidence: string;
}

export interface TeamPulseReport {
  reportId: string;
  generatedAt: string;
  audience: string;
  title: string;
  execSummary: {
    headline: string;
    points: string[];
  };
  members: TeamPulseMember[];
  executiveContext: {
    from: string;
    points: string[];
  };
  dataCoverage: {
    note: string;
    gaps: string[];
  };
}

export interface TeamPulseReportResponse {
  report: TeamPulseReport;
}

// 活動量（TP-10）。契約は packages/core/src/member-activity/types.ts と同期

export interface TeamPulseProviderCoverage {
  provider: string;
  /** linked = 誰の分か分かる / partial = 一部 / unlinked = 紐づけ不能 / absent = 未接続 */
  state: 'linked' | 'partial' | 'unlinked' | 'absent';
  totalDocuments: number;
  attributedDocuments: number;
  identityCount: number;
  /** 計測開始日。これより前は数値が無い（活動が無かったのではない） */
  measuredFrom: string | null;
  note: string | null;
}

export interface TeamPulseActivityMetric {
  memberId: string;
  periodType: 'week' | 'month' | 'quarter';
  periodStart: string;
  periodEnd: string;
  provider: string;
  metric: string;
  value: number;
}

export interface TeamPulseActivity {
  computedAt: string | null;
  periods: Array<{
    periodType: 'week' | 'month' | 'quarter';
    periodStart: string;
    periodEnd: string;
  }>;
  members: Array<{
    memberId: string;
    displayName: string;
    metrics: TeamPulseActivityMetric[];
    /**
     * TP-25: この人を紐づけられる情報源。ここに無い情報源は「対象外」であり、
     * 0 件（＝その期間の活動が無かった）とは意味が違う。
     * 古い応答には無いので、読み手側で省略を許容する。
     */
    linkedProviders?: string[];
  }>;
  coverage: TeamPulseProviderCoverage[];
}

// ─── Error Types ───

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const AUTH_TOKEN_UNAVAILABLE = 'auth_token_unavailable' as const;

export class AuthTokenUnavailableError extends ApiError {
  readonly code = AUTH_TOKEN_UNAVAILABLE;

  constructor() {
    super(
      'Authentication token unavailable - Retry or sign in again',
      0,
      { code: AUTH_TOKEN_UNAVAILABLE },
    );
    this.name = 'AuthTokenUnavailableError';
  }
}

// ─── Core API Client ───

export interface ApiClientOptions {
  getToken?: () => Promise<string | null>;
  /**
   * Defaults to required when getToken is configured and optional otherwise.
   * Public endpoints may explicitly opt into optional auth.
   */
  authMode?: 'required' | 'optional';
}

class ApiClient {
  private getToken?: () => Promise<string | null>;
  private authMode: 'required' | 'optional';

  constructor(options?: ApiClientOptions) {
    this.getToken = options?.getToken;
    this.authMode = options?.authMode ?? (this.getToken ? 'required' : 'optional');
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Build URL from base + path
    const url = buildApiUrl(path);

    const headers: Record<string, string> = {};

    // Only set Content-Type for JSON body (not FormData/Blob)
    const body = options.body;
    if (body && typeof body === 'string') {
      headers['Content-Type'] = 'application/json';
    }

    // Copy existing headers
    if (options.headers) {
      const existingHeaders = new Headers(options.headers);
      existingHeaders.forEach((value, key) => {
        headers[key] = value;
      });
    }

    // Add Bearer token when available. Protected clients fail before fetch when
    // Clerk cannot provide a token, rather than degrading into an anonymous API call.
    if (this.getToken) {
      let token: string | null = null;
      try {
        token = await this.getToken();
      } catch {
        if (this.authMode === 'required') {
          throw new AuthTokenUnavailableError();
        }
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (this.authMode === 'required') {
        throw new AuthTokenUnavailableError();
      }
    } else if (this.authMode === 'required') {
      throw new AuthTokenUnavailableError();
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle error responses
    if (!response.ok) {
      // Try JSON first, fallback to text for error details
      let errorData: unknown = null;
      let errorMessage = `Request failed with status ${response.status}`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          errorData = await response.json();
          errorMessage = (errorData as { error?: string })?.error || errorMessage;
        } else {
          const text = await response.text();
          if (text) {
            errorMessage = text.slice(0, 200); // Limit error message length
            errorData = { text };
          }
        }
      } catch {
        // Ignore parsing errors, use default message
      }

      switch (response.status) {
        case 401:
          throw new ApiError('Unauthorized - Please log in', 401, errorData);
        case 403:
          throw new ApiError('Forbidden - You do not have permission', 403, errorData);
        case 404:
          throw new ApiError('Not found', 404, errorData);
        default:
          throw new ApiError(errorMessage, response.status, errorData);
      }
    }

    // Handle empty responses (204 No Content, etc.)
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return null as T;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : (null as T);
  }

  // ─── ACL API ───

  async getACLSettings(workspaceId?: string): Promise<DefaultACLSettings> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<DefaultACLSettings>(`/acl/settings${qs}`);
  }

  async updateACLSettings(settings: DefaultACLSettings, workspaceId?: string): Promise<DefaultACLSettings> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<DefaultACLSettings>(`/acl/settings${qs}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getACLCategories(workspaceId?: string): Promise<ACLCategory[]> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ categories: ACLCategory[] }>(`/acl/categories${qs}`);
    return response.categories;
  }

  async createACLCategory(category: CreateACLCategoryRequest, workspaceId?: string): Promise<ACLCategory> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<ACLCategory>(`/acl/categories${qs}`, {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

  async getShareRequests(workspaceId?: string): Promise<ShareRequest[]> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<ShareRequestsResponse>(`/acl/requests${qs}`);
    return response.requests;
  }

  async approveShareRequest(requestId: string, reason?: string, workspaceId?: string): Promise<ShareRequest> {
    const body: ApproveRejectRequest = { reason };
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ success: boolean; request: ShareRequest }>(
      `/acl/requests/${requestId}/approve${qs}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    return response.request;
  }

  async rejectShareRequest(requestId: string, reason?: string, workspaceId?: string): Promise<ShareRequest> {
    const body: ApproveRejectRequest = { reason };
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ success: boolean; request: ShareRequest }>(
      `/acl/requests/${requestId}/reject${qs}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    return response.request;
  }

  // ─── Adapters API ───

  async getAdapterOverview(workspaceId: string): Promise<AdaptersListResponse> {
    return this.request<AdaptersListResponse>(`/adapters?workspaceId=${encodeURIComponent(workspaceId)}`);
  }

  async getAdapters(workspaceId: string): Promise<AdapterInfo[]> {
    const response = await this.request<AdaptersListResponse>(
      `/adapters?workspaceId=${encodeURIComponent(workspaceId)}`
    );
    return response.adapters;
  }

  async getAdapterStatus(type: AdapterType, workspaceId: string): Promise<AdapterInfo> {
    return this.request<AdapterInfo>(
      `/adapters/${type}/status?workspaceId=${encodeURIComponent(workspaceId)}`
    );
  }

  async connectAdapter(
    type: AdapterType,
    workspaceId: string,
    options?: {
      dryRun?: boolean;
      config?: Record<string, unknown>;
      credentialsRef?: string;
      healthCheckUrl?: string;
    }
  ): Promise<AdapterConnectResponse> {
    const body: AdapterConnectRequest = {
      workspaceId,
      dryRun: options?.dryRun ?? false,
    };
    if (options?.config !== undefined) {
      body.config = options.config;
    }
    if (options?.credentialsRef !== undefined) {
      body.credentialsRef = options.credentialsRef;
    }
    if (options?.healthCheckUrl !== undefined) {
      body.healthCheckUrl = options.healthCheckUrl;
    }
    return this.request<AdapterConnectResponse>(`/adapters/${type}/connect`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async disconnectAdapter(
    type: AdapterType,
    workspaceId: string,
    dryRun = false,
  ): Promise<AdapterDisconnectResponse> {
    return this.request<AdapterDisconnectResponse>(`/adapters/${type}/disconnect`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, dryRun }),
    });
  }

  async startHeartbeat(workspaceId: string): Promise<{ success: boolean; message?: string }> {
    return this.request<{ success: boolean; message?: string }>(
      `/adapters/heartbeat/start?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'POST' },
    );
  }

  // ─── BOX API ───

  async uploadFile(input: BoxUploadInput): Promise<BoxUploadResponse> {
    const formData = new FormData();
    formData.append('file', input.file);
    formData.append('workspaceId', input.metadata.workspaceId);
    formData.append('filename', input.metadata.filename);
    formData.append('size', String(input.metadata.size));
    formData.append('mimeType', input.metadata.mimeType);
    if (input.metadata.tags && input.metadata.tags.length > 0) {
      formData.append('tags', JSON.stringify(input.metadata.tags));
    }
    if (input.metadata.category) {
      formData.append('category', input.metadata.category);
    }
    if (input.metadata.visibility) {
      formData.append('visibility', input.metadata.visibility);
    }

    return this.request<BoxUploadResponse>('/box/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getUploadStatus(uploadId: string): Promise<BoxStatusResponse> {
    return this.request<BoxStatusResponse>(`/box/status/${uploadId}`);
  }

  async getUploadHistory(
    workspaceId: string,
    page = 1,
    pageSize = 20,
  ): Promise<BoxHistoryResponse> {
    const query = new URLSearchParams({
      workspaceId,
      page: String(page),
      pageSize: String(pageSize),
    });
    return this.request<BoxHistoryResponse>(`/box/history?${query.toString()}`);
  }

  async cancelUpload(uploadId: string): Promise<boolean> {
    const response = await this.request<{ success: boolean }>(`/box/cancel/${uploadId}`, {
      method: 'DELETE',
    });
    return response.success;
  }

  // ─── Proposals API ───

  async getProposals(params?: {
    workspaceId?: string;
    status?: ProposalStatus[];
    templateId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ProposalListResponse> {
    const query = new URLSearchParams();
    if (params?.workspaceId) query.set('workspaceId', params.workspaceId);
    if (params?.status && params.status.length > 0) {
      query.set('status', params.status.join(','));
    }
    if (params?.templateId) query.set('templateId', params.templateId);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));

    const suffix = query.toString();
    const path = suffix ? `/proposals?${suffix}` : '/proposals';
    return this.request<ProposalListResponse>(path);
  }

  async getProposalDetail(id: string): Promise<ProposalDetailResponse> {
    return this.request<ProposalDetailResponse>(`/proposals/${id}`);
  }

  async approveProposal(id: string, reason?: string): Promise<ProposalActionResponse> {
    const body: ApproveRejectRequest = { reason };
    return this.request<ProposalActionResponse>(`/proposals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async rejectProposal(id: string, reason?: string): Promise<ProposalActionResponse> {
    const body: ApproveRejectRequest = { reason };
    return this.request<ProposalActionResponse>(`/proposals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ─── Workspaces API ───

  async getWorkspaceFeatures(workspaceId: string): Promise<WorkspaceFeaturesResponse> {
    return this.request<WorkspaceFeaturesResponse>(`/workspaces/${workspaceId}/features`);
  }

  async updateWorkspaceFeatures(
    workspaceId: string,
    features: Partial<WorkspaceFeaturesData>,
  ): Promise<WorkspaceFeaturesResponse> {
    return this.request<WorkspaceFeaturesResponse>(`/workspaces/${workspaceId}/features`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    });
  }

  async getWorkspaceStatus(workspaceId: string): Promise<WorkspaceStatusResponse> {
    return this.request<WorkspaceStatusResponse>(`/onboarding/workspaces/${workspaceId}/status`);
  }

  async createOnboardingWorkspace(
    input: CreateOnboardingWorkspaceRequest,
  ): Promise<OnboardingWorkspaceResponse> {
    return this.request<OnboardingWorkspaceResponse>('/onboarding/workspaces', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getOnboardingWorkspace(workspaceId: string): Promise<OnboardingWorkspaceResponse> {
    return this.request<OnboardingWorkspaceResponse>(
      `/onboarding/workspaces/${encodeURIComponent(workspaceId)}`
    );
  }

  async startOnboardingAutoSetup(
    workspaceId: string,
    preset: OnboardingPreset,
  ): Promise<OnboardingAutoSetupResponse> {
    return this.request<OnboardingAutoSetupResponse>(
      `/onboarding/workspaces/${encodeURIComponent(workspaceId)}/auto-setup`,
      {
        method: 'POST',
        body: JSON.stringify({ preset }),
      }
    );
  }

  // ─── Memory API ───

  async getMemoryCategories(): Promise<MemoryCategory[]> {
    const response = await this.request<{ categories: MemoryCategory[] }>('/memory/categories');
    return response.categories;
  }

  async createMemoryCategory(input: Pick<MemoryCategory, 'name' | 'description' | 'summary'>): Promise<MemoryCategory> {
    return this.request<MemoryCategory>('/memory/categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryItems(categoryId: string): Promise<MemoryItem[]> {
    const response = await this.request<{ items: MemoryItem[] }>(`/memory/categories/${categoryId}/items`);
    return response.items;
  }

  async createMemoryItem(
    categoryId: string,
    input: Pick<MemoryItem, 'title' | 'description' | 'tags'>
  ): Promise<MemoryItem> {
    return this.request<MemoryItem>(`/memory/categories/${categoryId}/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryResources(itemId: string): Promise<MemoryResource[]> {
    const response = await this.request<{ resources: MemoryResource[] }>(`/memory/items/${itemId}/resources`);
    return response.resources;
  }

  async createMemoryResource(
    itemId: string,
    input: Pick<MemoryResource, 'sourceType' | 'sourceId' | 'label' | 'excerpt' | 'url' | 'metadata'>
  ): Promise<MemoryResource> {
    return this.request<MemoryResource>(`/memory/items/${itemId}/resources`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryLinks(itemId: string): Promise<MemoryLink[]> {
    const response = await this.request<{ links: MemoryLink[] }>(`/memory/items/${itemId}/links`);
    return response.links;
  }

  async createMemoryLink(
    itemId: string,
    input: Pick<MemoryLink, 'toItemId' | 'relation'>
  ): Promise<MemoryLink> {
    return this.request<MemoryLink>(`/memory/items/${itemId}/links`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async exportMemoryAssets(): Promise<MemoryAssetExportResponse> {
    return this.request<MemoryAssetExportResponse>('/memory/export');
  }

  async search(input: SearchRequest): Promise<SearchResponse> {
    return this.request<SearchResponse>('/search', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  // ─── Workspace Admin API (Q-1) ───

  /**
   * WU-07: capability bootstrap（起動時 1 回）。
   * レスポンス契約は capability-config.ts / core admin.ts と同期。
   */
  async getMeBootstrap(): Promise<MeBootstrapResponse> {
    return this.request<MeBootstrapResponse>('/admin/me/bootstrap');
  }

  async listWorkspaces(): Promise<WorkspaceListResponse> {
    return this.request<WorkspaceListResponse>('/admin/workspaces');
  }

  async listVisibleWorkspaces(): Promise<VisibleWorkspaceListResponse> {
    return this.request<VisibleWorkspaceListResponse>('/admin/me/workspaces');
  }

  async getPrimaryWorkspace(): Promise<VisibleWorkspaceResponse> {
    return this.request<VisibleWorkspaceResponse>('/admin/me/workspaces/primary');
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMembersResponse> {
    return this.request<WorkspaceMembersResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members`
    );
  }

  async inviteMember(workspaceId: string, req: InviteMemberRequest): Promise<WorkspaceMember> {
    return this.request<WorkspaceMember>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members`,
      { method: 'POST', body: JSON.stringify(req) }
    );
  }

  async updateMemberRole(
    workspaceId: string,
    memberId: string,
    req: UpdateMemberRoleRequest
  ): Promise<WorkspaceMember> {
    return this.request<WorkspaceMember>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`,
      { method: 'PATCH', body: JSON.stringify(req) }
    );
  }

  async removeMember(workspaceId: string, memberId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`,
      { method: 'DELETE' }
    );
  }

  async getOverallStats(period: 'day' | 'week' | 'month' = 'week'): Promise<OverallStatsResponse> {
    return this.request<OverallStatsResponse>(`/admin/stats?period=${period}`);
  }

  async getBenchmarkCurationOverview(): Promise<BenchmarkCurationOverviewResponse> {
    return this.request<BenchmarkCurationOverviewResponse>('/admin/benchmark-curation/overview');
  }

  async getBenchmarkCurationQueue(params?: {
    limit?: number;
    includeClosed?: boolean;
  }): Promise<BenchmarkCurationQueueResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.includeClosed !== undefined) query.set('includeClosed', String(params.includeClosed));
    const suffix = query.toString();
    return this.request<BenchmarkCurationQueueResponse>(
      `/admin/benchmark-curation/queue${suffix ? `?${suffix}` : ''}`
    );
  }

  async listBenchmarkCurationPackets(params?: {
    status?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<BenchmarkCurationPacketListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<BenchmarkCurationPacketListResponse>(
      `/admin/benchmark-curation/packets${suffix ? `?${suffix}` : ''}`
    );
  }

  async getBenchmarkCurationPacket(packetId: string): Promise<BenchmarkCurationPacketDetailResponse> {
    return this.request<BenchmarkCurationPacketDetailResponse>(
      `/admin/benchmark-curation/packets/${encodeURIComponent(packetId)}`
    );
  }

  async updateBenchmarkCurationPacket(
    packetId: string,
    input: BenchmarkCurationPacketUpdateRequest,
  ): Promise<BenchmarkCurationPacketDetailResponse> {
    const response = await this.request<{ packet: BenchmarkCurationPacketDetailResponse["packet"] }>(
      `/admin/benchmark-curation/packets/${encodeURIComponent(packetId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
    return {
      packet: response.packet,
      queueMeta: null,
    };
  }

  async getJLocomoCurationOverview(): Promise<JLocomoCurationOverviewResponse> {
    return this.request<JLocomoCurationOverviewResponse>('/admin/j-locomo-curation/overview');
  }

  async getJLocomoCurationQueue(params?: {
    limit?: number;
    includeClosed?: boolean;
  }): Promise<JLocomoCurationQueueResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.includeClosed !== undefined) query.set('includeClosed', String(params.includeClosed));
    const suffix = query.toString();
    return this.request<JLocomoCurationQueueResponse>(
      `/admin/j-locomo-curation/queue${suffix ? `?${suffix}` : ''}`
    );
  }

  async listJLocomoCurationPackets(params?: {
    status?: string;
    slice?: string;
    split?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<JLocomoCurationPacketListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.slice) query.set('slice', params.slice);
    if (params?.split) query.set('split', params.split);
    if (params?.q) query.set('q', params.q);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<JLocomoCurationPacketListResponse>(
      `/admin/j-locomo-curation/packets${suffix ? `?${suffix}` : ''}`
    );
  }

  async getJLocomoCurationPacket(packetId: string): Promise<JLocomoCurationPacketDetailResponse> {
    return this.request<JLocomoCurationPacketDetailResponse>(
      `/admin/j-locomo-curation/packets/${encodeURIComponent(packetId)}`
    );
  }

  async updateJLocomoCurationPacket(
    packetId: string,
    input: JLocomoCurationPacketUpdateRequest,
  ): Promise<JLocomoCurationPacketDetailResponse> {
    return this.request<JLocomoCurationPacketDetailResponse>(
      `/admin/j-locomo-curation/packets/${encodeURIComponent(packetId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
  }

  async getJLocomoArtifactsStatus(): Promise<JLocomoArtifactsStatusResponse> {
    return this.request<JLocomoArtifactsStatusResponse>('/admin/j-locomo-curation/artifacts/status')
  }

  async getJLocomoReadiness(): Promise<JLocomoReadinessResponse> {
    return this.request<JLocomoReadinessResponse>('/admin/j-locomo-curation/readiness')
  }

  async validateJLocomoCuration(
    input: JLocomoCurationValidateRequest = {},
  ): Promise<JLocomoCurationValidateResponse> {
    return this.request<JLocomoCurationValidateResponse>('/admin/j-locomo-curation/actions/validate', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async compileJLocomoCuration(): Promise<JLocomoCurationCompileResponse> {
    return this.request<JLocomoCurationCompileResponse>('/admin/j-locomo-curation/actions/compile', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  async runJLocomoSmokeBenchmark(
    input: JLocomoCurationSmokeRunRequest,
  ): Promise<JLocomoCurationSmokeRunResponse> {
    return this.request<JLocomoCurationSmokeRunResponse>('/admin/j-locomo-curation/actions/smoke-run', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  // ─── KPI API ───

  async getKpiSummary(): Promise<KpiSummaryResponse> {
    return this.request<KpiSummaryResponse>('/kpi/summary')
  }

  async getKpiStats(period: 'week' | 'month' = 'week'): Promise<KpiStatsResponse> {
    return this.request<KpiStatsResponse>(`/kpi/stats?period=${period}`)
  }

  // ─── Monitoring API ───

  async getMonitoringMetrics(periodDays = 7): Promise<MonitoringMetricsResponse> {
    return this.request<MonitoringMetricsResponse>(`/monitoring/metrics?periodDays=${periodDays}`)
  }

  async getMonitoringHealth(): Promise<MonitoringHealthResponse> {
    return this.request<MonitoringHealthResponse>('/monitoring/health')
  }

  // ─── Catalog API (掌握マップ, CL-05) ───

  async getCatalogSummary(sfOrgId?: string): Promise<CatalogSummaryResponse> {
    const qs = sfOrgId ? `?sfOrgId=${encodeURIComponent(sfOrgId)}` : ''
    return this.request<CatalogSummaryResponse>(`/catalog/kpi${qs}`)
  }

  // ─── Freshness API (WU-06) ───

  async getFreshness(): Promise<FreshnessResponse> {
    return this.request<FreshnessResponse>('/freshness')
  }

  // ─── Funnel Dashboard API (GR-04c-4) ───
  // path は base 抜き（/funnel/...）。独自 fetch + /api 直書きは二重 prefix になるため禁止
  // （web-ui-api-client-pattern、api-catalog-kpi-monitoring.test.ts と同種の回帰を防ぐ）。

  async getFunnelOverview(months = 6): Promise<FunnelOverviewResponse> {
    return this.request<FunnelOverviewResponse>(
      `/funnel/overview?months=${encodeURIComponent(months)}`
    )
  }

  async getFunnelSegments(
    axis: FunnelSegmentAxis,
    months = 6
  ): Promise<FunnelSegmentsResponse> {
    return this.request<FunnelSegmentsResponse>(
      `/funnel/segments?axis=${encodeURIComponent(axis)}&months=${encodeURIComponent(months)}`
    )
  }

  async getFunnelPolicyEvents(months = 12): Promise<FunnelPolicyEventsResponse> {
    return this.request<FunnelPolicyEventsResponse>(
      `/funnel/policy-events?months=${encodeURIComponent(months)}`
    )
  }

  // ─── GIFT サマリー API (GSB) ───

  async getGiftSummaryOverview(): Promise<GiftSummaryOverviewResponse> {
    return this.request<GiftSummaryOverviewResponse>('/gift-summary/overview')
  }

  async getGiftSummaryCalls(month?: string): Promise<GiftSummaryCallsResponse> {
    const qs = month ? `?month=${encodeURIComponent(month)}` : ''
    return this.request<GiftSummaryCallsResponse>(`/gift-summary/calls${qs}`)
  }

  async getGiftSummaryAiSdr(): Promise<GiftSummaryAiSdrResponse> {
    return this.request<GiftSummaryAiSdrResponse>('/gift-summary/ai-sdr')
  }

  /** AX サマリー。query は `range=this_month&from=&to=` のような形（先頭の ? は付けない）。 */
  async getGiftSummaryAx(query?: string): Promise<GiftSummaryAxResponse> {
    const qs = query ? `?${query}` : ''
    return this.request<GiftSummaryAxResponse>(`/gift-summary/ax${qs}`)
  }

  /** AX サマリーの各社分析（商談パックの一覧表から）。query は getGiftSummaryAx と同じ。 */
  async getGiftSummaryAxCompanies(query?: string): Promise<GiftSummaryAxCompaniesResponse> {
    const qs = query ? `?${query}` : ''
    return this.request<GiftSummaryAxCompaniesResponse>(`/gift-summary/ax/companies${qs}`)
  }

  async listGiftDealPacks(): Promise<GiftSummaryDealPacksResponse> {
    return this.request<GiftSummaryDealPacksResponse>('/gift-summary/deal-packs')
  }

  /** 商談パックの HTML 本文（Bearer 付き・生テキスト）。not_connected / 404 は null。 */
  async getGiftDealPackHtml(pack: string, file: string): Promise<{ html: string } | null> {
    const url = buildApiUrl(
      `/gift-summary/deal-packs/${encodeURIComponent(pack)}/${encodeURIComponent(file)}`
    )
    const headers: Record<string, string> = {}
    if (this.getToken) {
      let token: string | null = null
      try {
        token = await this.getToken()
      } catch {
        // 取得失敗時は token = null のまま（Bearer なしで fetch し 401/404 に任せる）
      }
      if (token) headers['Authorization'] = `Bearer ${token}`
    }
    const res = await fetch(url, { headers })
    if (!res.ok) {
      if (res.status === 404) return null
      throw new ApiError(`Request failed with status ${res.status}`, res.status)
    }
    const text = await res.text()
    return { html: text }
  }

  // ─── Team Pulse API (TP-02) ───

  async getTeamPulseReport(): Promise<TeamPulseReportResponse> {
    return this.request<TeamPulseReportResponse>('/team-pulse/report')
  }

  async getTeamPulseActivity(): Promise<TeamPulseActivity> {
    return this.request<TeamPulseActivity>('/team-pulse/activity')
  }

  // ─── Weekly Meeting Center API ───

  async getMeetingCenterLatest(
    workspaceId: string,
  ): Promise<MeetingSnapshotResponse | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<MeetingSnapshotResponse | null>(
        `/meeting-center/latest?${query.toString()}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  async getMeetingCenterHistory(
    workspaceId: string,
  ): Promise<MeetingSnapshotHistoryResponse> {
    const query = new URLSearchParams({ workspaceId })
    return this.request<MeetingSnapshotHistoryResponse>(
      `/meeting-center/history?${query.toString()}`,
    )
  }

  /** 前週対比用: 指定週の最新 snapshot を取得(無い週は null)。WMU-02 */
  async getMeetingCenterWeek(
    workspaceId: string,
    weekOf: string,
  ): Promise<MeetingSnapshotResponse | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<MeetingSnapshotResponse | null>(
        `/meeting-center/week/${encodeURIComponent(weekOf)}?${query.toString()}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  async getMeetingCenterSnapshot(
    workspaceId: string,
    snapshotId: string,
  ): Promise<MeetingSnapshotResponse | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<MeetingSnapshotResponse | null>(
        `/meeting-center/snapshots/${encodeURIComponent(snapshotId)}?${query.toString()}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  // ─── Meeting board API ───
  //
  // ボードの中身は deploy 供給（docs/customer/board/）で、UI は形だけ知っていればよい。
  // 型は呼び出し側が渡す。ここに業務語彙の型を作らないのは黄金律のため。

  /** タブ一覧。未配置 deploy では空配列が返る。 */
  async getBoardTabs<T>(workspaceId: string): Promise<T> {
    const query = new URLSearchParams({ workspaceId })
    return this.request<T>(`/meeting-board/tabs?${query.toString()}`)
  }

  /** タブ単位のボード断片。そのタブが持たない断片は null。 */
  async getBoardSection<T>(
    workspaceId: string,
    section: 'previous' | 'progress' | 'metrics' | 'daily',
    tab: string,
  ): Promise<T | null> {
    const query = new URLSearchParams({ workspaceId, tab })
    try {
      return await this.request<T>(`/meeting-board/${section}?${query.toString()}`)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  /** タブ横断の集計セット。 */
  async getBoardCohort<T>(workspaceId: string): Promise<T | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<T>(`/meeting-board/cohort?${query.toString()}`)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  async getBoardDocuments<T>(workspaceId: string, tab: string): Promise<T> {
    const query = new URLSearchParams({ workspaceId, tab })
    return this.request<T>(`/meeting-board/documents?${query.toString()}`)
  }

  async getBoardDocument(
    workspaceId: string,
    key: string,
  ): Promise<{ key: string; html: string }> {
    const query = new URLSearchParams({ workspaceId })
    return this.request<{ key: string; html: string }>(
      `/meeting-board/documents/${encodeURIComponent(key)}?${query.toString()}`,
    )
  }

  async getBoardReportDates<T>(workspaceId: string, reportType: string): Promise<T> {
    const query = new URLSearchParams({ workspaceId })
    return this.request<T>(
      `/meeting-board/reports/${encodeURIComponent(reportType)}/dates?${query.toString()}`,
    )
  }

  async getBoardReport<T>(
    workspaceId: string,
    reportType: string,
    date?: string,
  ): Promise<T> {
    const query = new URLSearchParams({ workspaceId })
    if (date) query.set('date', date)
    return this.request<T>(
      `/meeting-board/reports/${encodeURIComponent(reportType)}?${query.toString()}`,
    )
  }

  // ─── GIFT Operations API ───

  async getSourceReadProfile(workspaceId: string, principalUserId: string): Promise<SourceReadProfileState> {
    return this.request<SourceReadProfileState>(`/admin/workspaces/${encodeURIComponent(workspaceId)}/operators/${encodeURIComponent(principalUserId)}/read-profile`)
  }

  async setSourceReadProfile(workspaceId: string, principalUserId: string, profile: SourceReadProfile): Promise<unknown> {
    return this.request(`/admin/workspaces/${encodeURIComponent(workspaceId)}/operators/${encodeURIComponent(principalUserId)}/read-profile`, {
      method: 'PUT', headers: mutationHeaders(), body: JSON.stringify({ profile }),
    })
  }

  async listGiftOperators(workspaceId: string): Promise<OperatorsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ operators: RawOperationsPrincipal[] }>(
      `/admin/operators?${query.toString()}`,
    )
    return {
      operators: (response.operators ?? []).map(mapOperationsPrincipal),
    }
  }

  async onboardGiftOperator(
    workspaceId: string,
    input: OnboardGiftOperatorInput,
  ): Promise<OnboardGiftOperatorResult> {
    const response = await this.request<
      | { operator: RawOperationsPrincipal; alreadyActive: boolean }
      | { alreadyApplied: true; code: 'duplicate_request' }
    >('/admin/operators/onboard', {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify({ workspaceId, ...input }),
    })
    if (!('operator' in response)) {
      return {
        operator: null,
        alreadyActive: true,
        alreadyApplied: true,
      }
    }
    const principal = response.operator
    return {
      operator: mapOperationsPrincipal(principal),
      alreadyActive: response.alreadyActive,
      alreadyApplied: false,
    }
  }

  async offboardGiftOperator(workspaceId: string, operatorId: string): Promise<void> {
    await this.request<null>(
      `/admin/operators/${encodeURIComponent(operatorId)}/offboard`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async issueGiftMcpCredential(
    workspaceId: string,
    input: IssueMcpCredentialInput,
  ): Promise<IssuedMcpCredential> {
    const response = await this.request<{
      mcpCredential: ManagedMcpCredential
      rawKey: string
    }>('/admin/mcp-credentials/issue', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({
        workspaceId,
        principalUserId: input.operatorId,
        name: input.name,
        ...(input.mode === 'inherit' ? { mode: 'inherit' } : {
          ...(input.mode === 'policies' ? { mode: 'policies' } : {}),
          policies: buildPresetPolicyEntries(input.profile),
        }),
      }),
    })
    return { credential: response.mcpCredential, rawKey: response.rawKey }
  }

  async reissueGiftMcpCredential(workspaceId: string, credentialId: string): Promise<IssuedMcpCredential> {
    const response = await this.request<{ mcpCredential: ManagedMcpCredential; rawKey: string }>(
      `/admin/mcp-credentials/${encodeURIComponent(credentialId)}/reissue`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
    return { credential: response.mcpCredential, rawKey: response.rawKey }
  }

  async revokeGiftMcpCredential(workspaceId: string, credentialId: string): Promise<void> {
    await this.request<null>(
      `/admin/mcp-credentials/${encodeURIComponent(credentialId)}/revoke`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async listMeetingSources(workspaceId: string): Promise<MeetingSourcesResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ items: RawMeetingSource[] }>(
      `/admin/meeting-sources?${query.toString()}`,
    )
    return {
      sources: response.items ?? [],
    }
  }

  async updateMeetingSource(
    workspaceId: string,
    sourceId: string,
    input: UpdateMeetingSourceInput,
  ): Promise<void> {
    await this.request<null>(
      `/admin/meeting-sources/${encodeURIComponent(sourceId)}`,
      {
        method: 'PUT',
        headers: mutationHeaders(),
        body: JSON.stringify({ ...input, workspaceId }),
      },
    )
  }

  async testMeetingSource(workspaceId: string, sourceId: string): Promise<MeetingSourceTestResult> {
    return this.request<MeetingSourceTestResult>(
      `/admin/meeting-sources/${encodeURIComponent(sourceId)}/test`,
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    )
  }

  async syncMeetingImports(
    workspaceId: string,
    input: { sourceConfigId: string; provider: 'google_meet' | 'zoom_meeting'; externalMeetingId: string; externalArtifactId: string },
  ): Promise<void> {
    await this.request<null>('/admin/meeting-imports/sync', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ workspaceId, ...input }),
    })
  }

  async listMeetingImports(workspaceId: string): Promise<MeetingImportsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ items: RawMeetingImport[] }>(
      `/admin/meeting-imports?${query.toString()}`,
    )
    return {
      imports: (response.items ?? []).map((run) => ({
        id: run.id,
        provider: run.provider,
        state: run.status,
        detectedAt: run.detectedAt,
        completedAt: run.reviewedAt,
        reasonCode: run.errorCode,
        candidates: (run.candidates ?? []).map((candidate) => ({
          id: candidate.id,
          kind: candidate.kind,
          summary: candidate.summary,
          confidence: candidate.confidence,
          reviewStatus: candidate.reviewStatus === 'pending'
            ? 'review_required'
            : candidate.reviewStatus,
        })),
      })),
    }
  }

  async getMeetingImportApprovalTarget(
    workspaceId: string,
  ): Promise<MeetingImportApprovalTarget | null> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ target: MeetingImportApprovalTarget | null }>(
      `/admin/meeting-imports/approval-target?${query.toString()}`,
    )
    return response.target
  }

  async approveMeetingImport(
    workspaceId: string,
    importId: string,
    expected: {
      expectedWeekOf: string
      expectedSnapshotId: string
      expectedRevision: number
      targetWorkstreamId: string
    },
  ): Promise<void> {
    await this.request<null>(
      `/admin/meeting-imports/${encodeURIComponent(importId)}/approve`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId, ...expected }) },
    )
  }

  async rejectMeetingImport(workspaceId: string, importId: string): Promise<void> {
    await this.request<null>(
      `/admin/meeting-imports/${encodeURIComponent(importId)}/reject`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async listGiftAuditEvents(workspaceId: string): Promise<GiftAuditEventsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ events: RawOperationsAuditEvent[] }>(
      `/admin/audit-events?${query.toString()}`,
    )
    return {
      events: (response.events ?? []).map((event) => ({
        id: event.id,
        action: event.action,
        actorLabel: event.actorUserId,
        targetLabel: event.resourceId,
        occurredAt: event.timestamp,
        summary: typeof event.metadata.operation === 'string' ? event.metadata.operation : event.action,
      })),
    }
  }

  // ─── Access Management API (UAC-08b) ───

  async listAccessPolicies(workspaceId: string): Promise<AccessPoliciesListResponse> {
    return this.request<AccessPoliciesListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies`
    )
  }

  async createAccessPolicy(
    workspaceId: string,
    input: CreateAccessPolicyRequest,
  ): Promise<AccessPolicy> {
    const response = await this.request<{ policy: AccessPolicy }>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    )
    return response.policy
  }

  async deleteAccessPolicy(workspaceId: string, policyId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies/${encodeURIComponent(policyId)}`,
      { method: 'DELETE' }
    )
  }

  async listMcpTokens(workspaceId: string): Promise<McpTokensListResponse> {
    return this.request<McpTokensListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens`
    )
  }

  async createMcpToken(
    workspaceId: string,
    input: CreateMcpTokenRequest,
  ): Promise<CreateMcpTokenResponse> {
    return this.request<CreateMcpTokenResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    )
  }

  async deleteMcpToken(workspaceId: string, tokenId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens/${encodeURIComponent(tokenId)}`,
      { method: 'DELETE' }
    )
  }

  /** UCX-05: 同じ userSlot/policy を維持したまま新鍵を発行し、旧鍵を失効する。 */
  async reissueMcpToken(workspaceId: string, tokenId: string): Promise<CreateMcpTokenResponse> {
    return this.request<CreateMcpTokenResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens/${encodeURIComponent(tokenId)}/reissue`,
      { method: 'POST' }
    )
  }

  // ─── userSlot 選択肢 API (UCX-04) ───

  async listUserSlots(workspaceId: string): Promise<UserSlotsListResponse> {
    return this.request<UserSlotsListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/user-slots`
    )
  }
}

// ─── Singleton Instance ───

let apiClientInstance: ApiClient | null = null;

export function createApiClient(options?: ApiClientOptions): ApiClient {
  apiClientInstance = new ApiClient(options);
  return apiClientInstance;
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error('API client not initialized. Call createApiClient() first.');
  }
  return apiClientInstance;
}

// ─── Export Types ───

export type {
  BoxUploadInput,
  DefaultACLSettings,
  ACLCategory,
  CreateACLCategoryRequest,
  ShareRequest,
  AdapterType,
  AdapterInfo,
  AdapterConnectResponse,
  FileUploadMetadata,
  BoxUploadResponse,
  BoxStatusResponse,
  BoxHistoryItem,
  ProposalListResponse,
  ProposalDetailResponse,
  ProposalActionResponse,
  ProposalStatus,
  MemoryCategory,
  MemoryItem,
  MemoryLink,
  MemoryResource,
  MemoryAssetExportResponse,
  SearchRequest,
  SearchResponse,
  SearchResultItem,
  SearchStrategy,
  WorkspaceFeaturesData,
  WorkspaceFeaturesResponse,
  WorkspaceStatusResponse,
  CreateOnboardingWorkspaceRequest,
  OnboardingWorkspaceResponse,
  OnboardingAutoSetupResponse,
  OnboardingPreset,
  VisibleWorkspaceInfo,
  VisibleWorkspaceListResponse,
  VisibleWorkspaceResponse,
  BenchmarkCurationOverviewResponse,
  BenchmarkCurationPacketListResponse,
  BenchmarkCurationPacketListItem,
  BenchmarkCurationPacketDetailResponse,
  BenchmarkCurationPacketDetail,
  BenchmarkCurationQueueResponse,
  BenchmarkCurationQueueEntry,
  BenchmarkCurationPacketUpdateRequest,
  JLocomoCurationOverviewResponse,
  JLocomoCurationPacketListResponse,
  JLocomoCurationPacketListItem,
  JLocomoCurationPacketDetailResponse,
  JLocomoCurationPacketDetail,
  JLocomoCurationQueueResponse,
  JLocomoCurationQueueEntry,
  JLocomoCurationPacketUpdateRequest,
  JLocomoArtifactsStatusResponse,
  JLocomoCurationValidateRequest,
  JLocomoCurationValidateResponse,
  JLocomoCurationCompileResponse,
  JLocomoCurationSmokeRunRequest,
  JLocomoCurationSmokeRunResponse,
  AccessPolicy,
  AccessPoliciesListResponse,
  CreateAccessPolicyRequest,
  McpToken,
  McpTokensListResponse,
  CreateMcpTokenRequest,
  CreateMcpTokenResponse,
  UserSlotsListResponse,
  MeBootstrapResponse,
};
